import { useMemo, useState } from "react";
import { RouteMap } from "./RouteMap";
import { LapProfileChart } from "./LapProfileChart";
import { enrichLaps, gpsPointsForLap, buildLapChartData } from "../lib/laps";
import { hasValidDistance } from "../lib/chartAxis";
import { fmtDistance, fmtDuration, fmtSpeed } from "../lib/format";
import { usesPaceAnalytics } from "../lib/analyticsRate";
import { fmtIF, fmtEF } from "../lib/powerMetrics";
import { METRIC_COLORS } from "../lib/designTokens";
import { zoneColorForValue } from "../lib/zones";

const PowerBar = ({ watts, maxWatts, ftp }) => {
  if (watts == null) return <span className="text-dark-text-muted">—</span>;
  const pct = maxWatts > 0 ? (watts / maxWatts) * 100 : 0;
  const ftpPct = ftp ? Math.round((watts / ftp) * 100) : null;
  const barColor = zoneColorForValue(watts, "power", ftp) || METRIC_COLORS.power;
  return (
    <div className="flex items-center gap-2 justify-end">
      <span className="tabular-nums w-10 text-right" style={{ color: barColor }}>{watts}</span>
      <div className="w-20 h-2 bg-dark-border relative">
        <div
          className="h-full"
          style={{ width: `${Math.min(pct, 100)}%`, background: barColor }}
        />
      </div>
      {ftpPct != null && (
        <span className="text-dark-text-muted tabular-nums w-8 text-right text-[10px]">
          {ftpPct}%
        </span>
      )}
    </div>
  );
};

export const LapsView = ({
  activity,
  samples,
  userFtp,
  userMaxHr,
  unitSystem = "metric",
}) => {
  const [selectedLap, setSelectedLap] = useState(null);

  const hasDistance = hasValidDistance(samples);

  const enrichedLaps = useMemo(
    () => enrichLaps(activity.laps, samples, userFtp),
    [activity.laps, samples, userFtp]
  );

  const showPowerMetrics = activity.has_power && userFtp;

  const maxAvgPower = useMemo(
    () => Math.max(...enrichedLaps.map((l) => l.avg_power || 0), 1),
    [enrichedLaps]
  );

  const lapSlice = useMemo(() => {
    if (!selectedLap) return null;
    return {
      startIdx: selectedLap.startIdx,
      endIdx: selectedLap.endIdx,
      startM: selectedLap.start_distance_m ?? 0,
      endM: selectedLap.end_distance_m ?? 0,
    };
  }, [selectedLap]);

  const chartData = useMemo(
    () => buildLapChartData(samples, hasDistance, lapSlice, unitSystem),
    [samples, hasDistance, lapSlice, unitSystem]
  );

  const hasSpeed = samples.some((s) => s.speed_ms != null);

  const gpsPoints = useMemo(
    () =>
      samples
        .filter((s) => s.lat != null && s.lon != null)
        .map((s) => [s.lat, s.lon]),
    [samples]
  );

  const highlightPoints = useMemo(() => {
    if (!selectedLap) return null;
    const pts = gpsPointsForLap(samples, selectedLap);
    return pts.length > 1 ? pts : null;
  }, [selectedLap, samples]);

  const showMap = gpsPoints.length > 1;
  const hasFileLaps = (activity.laps?.length ?? 0) > 0;
  const paceMode = usesPaceAnalytics(activity.sport);

  return (
    <div className="space-y-4" data-testid="laps-view">
      {/* Map on top, full width */}
      {showMap && (
        <RouteMap
          points={gpsPoints}
          highlightPoints={highlightPoints}
          compact
        />
      )}

      {/* Full-ride or selected-lap profile — all metrics in one block */}
      <LapProfileChart
        key={selectedLap?.lapNumber ?? "full"}
        data={chartData}
        lapStats={selectedLap}
        sport={activity.sport}
        hasPower={activity.has_power}
        hasHr={activity.has_hr}
        hasCadence={activity.has_cadence}
        hasSpeed={hasSpeed}
        totalDistanceKm={activity.total_distance_m}
        testId="lap-profile-chart"
        unitSystem={unitSystem}
      />

      {/* Lap splits table — only when the file includes lap data */}
      {hasFileLaps && (
      <div className="app-card overflow-hidden" data-testid="laps-table">
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-[var(--border-primary)]">
          <div className="card-label">File Laps</div>
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h3 className="section-title mt-0.5">
              Laps · {enrichedLaps.length}
            </h3>
            {showPowerMetrics && (
              <span className="meta-text">
                FTP {userFtp} W · NP / IF / TSS enabled
              </span>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead className="bg-[var(--surface-secondary)] text-[10px] tracking-[0.15em] uppercase text-dark-text-secondary border-b border-[var(--border-primary)] sticky top-0">
              <tr>
                <th className="text-left p-3">Lap</th>
                <th className="text-right p-3">Distance</th>
                <th className="text-right p-3">Gradient</th>
                <th className="text-right p-3">Time</th>
                <th className="text-right p-3">{paceMode ? "Pace" : "Speed"}</th>
                <th className="text-right p-3">Avg Power</th>
                {showPowerMetrics && (
                  <>
                    <th className="text-right p-3">NP</th>
                    <th className="text-right p-3">IF</th>
                    <th className="text-right p-3">TSS</th>
                    {activity.has_hr && <th className="text-right p-3">Pw:Hr</th>}
                  </>
                )}
                <th className="text-right p-3">Power</th>
                {activity.has_hr && <th className="text-right p-3">HR</th>}
                {activity.has_cadence && <th className="text-right p-3">Cadence</th>}
              </tr>
            </thead>
            <tbody>
              {enrichedLaps.map((lap) => {
                const isSelected = selectedLap?.lapNumber === lap.lapNumber;
                return (
                  <tr
                    key={lap.lapNumber}
                    onClick={() =>
                      setSelectedLap(isSelected ? null : lap)
                    }
                    className={`border-b border-[var(--border-primary)]/50 cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-brand-primary/10 border-l-[3px] border-l-brand-primary"
                        : "hover:bg-[var(--surface-hover)] even:bg-[var(--surface-secondary)]/40"
                    }`}
                    data-testid={`lap-row-${lap.lapNumber}`}
                  >
                    <td className="p-3 text-dark-text-primary">{lap.lapNumber}</td>
                    <td className="p-3 text-right">{fmtDistance(lap.distance_m, unitSystem)}</td>
                    <td className="p-3 text-right">
                      {lap.elevation_m != null ? (
                        <span
                          style={{
                            color:
                              lap.elevation_m > 0
                                ? "#31B978"
                                : lap.elevation_m < 0
                                  ? "#FF3B30"
                                  : "var(--app-text-muted)",
                          }}
                        >
                          {lap.elevation_m > 0 ? "+" : ""}
                          {lap.elevation_m} m
                          {lap.gradient_pct != null && (
                            <span className="text-dark-text-muted ml-1">
                              ({lap.gradient_pct}%)
                            </span>
                          )}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-3 text-right">{fmtDuration(lap.duration_seconds)}</td>
                    <td className="p-3 text-right">
                      {fmtSpeed(lap.avg_speed_ms, activity.sport, unitSystem)}
                    </td>
                    <td
                      className="p-3 text-right"
                      style={{
                        color:
                          lap.avg_power
                            ? zoneColorForValue(lap.avg_power, "power", userFtp) || METRIC_COLORS.power
                            : "var(--app-text-muted)",
                      }}
                    >
                      {lap.avg_power ? `${lap.avg_power} w` : "—"}
                    </td>
                    {showPowerMetrics && (
                      <>
                        <td className="p-3 text-right" style={{ color: METRIC_COLORS.power }}>
                          {lap.normalized_power ? `${lap.normalized_power} w` : "—"}
                        </td>
                        <td className="p-3 text-right text-dark-text-secondary">
                          {fmtIF(lap.intensity_factor)}
                        </td>
                        <td className="p-3 text-right" style={{ color: METRIC_COLORS.tss }}>
                          {lap.tss != null ? lap.tss : "—"}
                        </td>
                        {activity.has_hr && (
                          <td className="p-3 text-right text-dark-text-secondary">
                            {fmtEF(lap.efficiency_factor)}
                          </td>
                        )}
                      </>
                    )}
                    <td className="p-3">
                      <PowerBar
                        watts={lap.avg_power}
                        maxWatts={maxAvgPower}
                        ftp={userFtp}
                      />
                    </td>
                    {activity.has_hr && (
                      <td
                        className="p-3 text-right"
                        style={{
                          color:
                            lap.avg_hr
                              ? zoneColorForValue(lap.avg_hr, "hr", userMaxHr || 190) || "#FF3B30"
                              : "var(--app-text-muted)",
                        }}
                      >
                        {lap.avg_hr ? `${lap.avg_hr} bpm` : "—"}
                      </td>
                    )}
                    {activity.has_cadence && (
                      <td className="p-3 text-right text-[#AF52DE]">
                        {lap.avg_cadence ? `${lap.avg_cadence} rpm` : "—"}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {selectedLap && (
          <div className="px-5 sm:px-6 py-3 border-t border-[var(--border-primary)] meta-text">
            Lap {selectedLap.lapNumber} selected — click again to deselect
          </div>
        )}
      </div>
      )}

      {!hasFileLaps && (
        <p className="text-[10px] tracking-[0.2em] uppercase text-dark-text-muted px-1">
          No lap splits in this file — full ride profile shown above
        </p>
      )}
    </div>
  );
};
